import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { reduce } from 'lodash';
import { TcoEntities } from 'client/data/models/cost-to-own';
import { LABELS_MAP } from 'site-modules/cost-to-own/constants/labels-map';
import { getPriceString } from 'site-modules/shared/utils/price-utils';

import './costs-chart.scss';

const calculatePercentShare = (total, value) => (value / total) * 100;

// Get keys only needed for chart and return them in specific order
const formChartSectionsData = data => {
  const { depreciation, taxesAndFees, financeInterest, fuel, insurance, maintenance, repairs } = data;
  return { depreciation, taxesAndFees, financeInterest, fuel, insurance, repairs, maintenance };
};

export function CostsChart({ data, isMobile, className }) {
  const chartSectionsData = formChartSectionsData(data);
  const transformedChartData = reduce(
    chartSectionsData,
    (result, value, key) => [
      ...result,
      {
        value: getPriceString(value),
        label: LABELS_MAP[key],
        percentShare: calculatePercentShare(data.tcoPrice, value),
      },
    ],
    []
  );

  return (
    <figure
      className={classnames(
        'costs-chart d-flex flex-column flex-md-row',
        isMobile ? 'vertical mx-auto' : 'horizontal',
        className
      )}
      style={isMobile ? { height: '360px', width: '322px' } : { height: '194px' }}
    >
      <figcaption className="visually-hidden">Ownership Costs: 5-Year Total</figcaption>
      {transformedChartData.map(({ percentShare, label, value }, index) => {
        const isEven = index % 2 !== 0;
        const styles = {
          ...(isMobile ? { height: `${percentShare}%` } : { width: `${percentShare}%` }),
          ...(isEven ? { alignSelf: `flex-start` } : { alignSelf: `flex-end` }),
        };

        const pointer = (
          <div
            className="pointer d-flex flex-md-column align-items-center justify-content-center"
            style={isMobile ? { width: '27px' } : { height: '27px' }}
          >
            {isEven && <div className="circle" />}
            <div className="line" />
            {!isEven && <div className="circle" />}
          </div>
        );

        const caption = (
          <div className="section-caption d-flex d-md-block">
            {!isEven && pointer}
            <div className="caption d-flex flex-column justify-content-center align-items-center mx-0_5 mx-md-0">
              <p className="heading-6-sub text-gray-darker text-nowrap mb-0">{label}</p>
              <p className="heading-6 mb-0 mb-md-0_25">{value}</p>
            </div>
            {isEven && pointer}
          </div>
        );

        return (
          <div key={label} className="chart-section d-flex d-md-block justify-content-center" style={styles}>
            {isEven && caption}
            <div
              className={`chart-block background-tone-${index + 1}`}
              style={isMobile ? { width: '52px' } : { height: '52px' }}
            />
            {!isEven && caption}
          </div>
        );
      })}
    </figure>
  );
}

CostsChart.propTypes = {
  data: TcoEntities.Tco,
  className: PropTypes.string,
  isMobile: PropTypes.bool,
};

CostsChart.defaultProps = {
  data: {},
  className: null,
  isMobile: false,
};
